// 2026-02-24 — iter_62 — Print queue worker.
//
// Lives in the logged-in POS browser tab. Every ~4s polls
// `/api/print/jobs/pending`, fetches ESC/POS bytes via the existing
// receipt/docket endpoints, sends them via the existing printBridge,
// then POSTs `/api/print/jobs/{id}/printed` (or /failed).
//
// Design constraints:
//   - Only runs in /pos (manually attached via usePrintQueueWorker()).
//   - Respects the existing venue.settings.printing.enabled killswitch
//     (no auto-print when the operator has explicitly disabled printing).
//   - Single concurrent run guard so two poll cycles can't race.
//   - Print is single-attempt per cycle — the unique-index on the
//     backend means even if we ever did race, no duplicate docket gets
//     queued. Failures bubble up to the operator via a toast + retry
//     button in Settings → Print queue.
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { printBridge } from "./printBridge";

const POLL_MS = 4000;
const _state = { busy: false };

// iter_115 (2026-06-10) — Stable per-tab device id for telemetry.
//
// Persisted in localStorage so the same browser tab (POS terminal vs
// owner laptop vs kitchen iPad) keeps the same id across reloads.
// Generated lazily on first use. NEVER contains user/customer data.
function _stableDeviceId() {
  try {
    const KEY = "mm_print_device_id";
    let v = (typeof localStorage !== "undefined") ? localStorage.getItem(KEY) : null;
    if (!v) {
      const rand = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      v = rand;
      try { if (typeof localStorage !== "undefined") localStorage.setItem(KEY, v); }
      catch (_e) { /* private mode */ }
    }
    return v;
  } catch (_e) {
    return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

async function _processOne(job, venueSettings) {
  // iter_104 (2026-06-08) P0 HOTFIX — Pre-flight guard.
  //
  // Since iter_101 mounted this worker in Layout.jsx, every authenticated
  // browser tab on every device polls /print/jobs/pending. Devices
  // without a paired printer MUST NOT race to claim+fail jobs, otherwise
  // they flip status="failed" and starve the real printer terminal.
  //
  // Defence-in-depth: even if a downstream bridge call somehow returns
  // {ok:false, skipped:undefined} for a "no printer here" condition, we
  // short-circuit BEFORE fetching ESC/POS bytes and before any /failed
  // POST. The printBridge skipped contract is the primary guarantee;
  // this guard is the belt-and-braces backup.
  if (!printBridge.supported()) return false;
  const _bridgeStatus = printBridge.status();
  if (!_bridgeStatus?.paired) return false;

  // iter_115 (2026-06-10) — Lifecycle event sink. Fire-and-forget;
  // failures are swallowed so a transient backend hiccup can never
  // silence the printer. Stable per-tab device id surfaces which
  // browser session claimed each job — critical for diagnosing the
  // F-O failure modes in production. Diagnostic payloads from the
  // bridge are attached so each event carries the WHY (USB pairing
  // state, transferOut error, endpoint info) not just the WHAT.
  const _devId = _stableDeviceId();
  const postEvent = (event, extra) => {
    try {
      const body = { event, device_id: _devId };
      if (extra && typeof extra === "object") {
        if (extra.reason !== undefined) body.reason = extra.reason;
        if (extra.error !== undefined) body.error = extra.error;
        if (extra.diag !== undefined) body.diag = extra.diag;
      }
      api.post(`/print/jobs/${job.id}/event`, body).catch(() => {});
    } catch (_e) { /* never block */ }
  };
  postEvent("worker_seen", { diag: (printBridge.diag && printBridge.diag()) || undefined });

  // Pick the right bridge fn based on job type.
  // iter_86 — kitchen_docket_update.* and customer_receipt_update.*
  // carry a uuid8 suffix per-edit so the unique (biz, order, type) index
  // doesn't collapse sequential edit reprints. Prefix-match here.
  // iter_88 — void_slip_void.<uuid> / void_slip_cancelled.<uuid> route
  // to printDocket-with-void-banner via the dedicated bridge fn.
  // iter_115 (2026-06-10) — customer_package_receipt is now prefix-
  // matched so iter_112's customer_package_receipt_update.<uuid8>
  // also routes correctly (previously fell into unknown_type and was
  // marked failed). Each branch posts a bridge_started event with
  // the branch reason for production diagnosis.
  let result;
  const _t = typeof job.type === "string" ? job.type : "";
  try {
    if (_t.startsWith("kitchen_docket_update")) {
      postEvent("bridge_started", { reason: "docket_update" });
      result = await printBridge.printDocketUpdate(job.order_id, { venueSettings });
    } else if (_t.startsWith("customer_receipt_update")) {
      postEvent("bridge_started", { reason: "receipt_update" });
      result = await printBridge.printReceiptUpdate(job.order_id, { venueSettings });
    } else if (_t.startsWith("void_slip_")) {
      const kind = _t.startsWith("void_slip_cancelled") ? "cancelled" : "void";
      postEvent("bridge_started", { reason: "void_slip" });
      result = await printBridge.printVoidSlip(job.order_id, { venueSettings, kind });
    } else if (_t.startsWith("kitchen_docket_course.")) {
      const course = job.course || _t.split(".")[1] || "main";
      postEvent("bridge_started", { reason: "kitchen_course" });
      result = await printBridge.printDocketCourse(job.order_id, course, { venueSettings });
    } else if (_t.startsWith("kitchen_docket_station.")) {
      // iter_96 (2026-06-10) — Per-station kitchen docket. Job carries
      // the original station label so the bridge sends ?station=<name>
      // and the backend filters items to that station only. Must
      // appear BEFORE the generic kitchen_docket branch so the prefix
      // match wins.
      const station = job.station || _t.split(".")[1] || "Kitchen";
      postEvent("bridge_started", { reason: "kitchen_docket_station" });
      result = await printBridge.printDocketStation(
        job.order_id, station, { venueSettings });
    } else if (_t === "kitchen_docket") {
      postEvent("bridge_started", { reason: "kitchen_docket" });
      result = await printBridge.printDocket(job.order_id, { venueSettings });
    } else if (_t.startsWith("customer_package_receipt")) {
      // iter_115 — Prefix match covers iter_102 base type + iter_112's
      // customer_package_receipt_update.<uuid8> reprints.
      postEvent("bridge_started", { reason: "package_receipt" });
      result = await printBridge.printPackageReceipt(job.order_id, { venueSettings });
    } else if (_t === "customer_receipt") {
      postEvent("bridge_started", { reason: "customer_receipt" });
      result = await printBridge.printReceipt(job.order_id, { venueSettings });
    } else {
      // Unknown type — mark failed so it doesn't loop forever.
      postEvent("bridge_failed", { reason: "unknown_type", error: _t });
      await api.post(`/print/jobs/${job.id}/failed`,
        { error: `unknown_type:${_t}`, device_id: _devId });
      return false;
    }
  } catch (e) {
    postEvent("bridge_failed", { error: (e && e.message) || "exception" });
    await api.post(`/print/jobs/${job.id}/failed`,
      { error: (e && e.message) || "exception", device_id: _devId });
    return false;
  }

  // Killswitch / no-webusb both look like benign skips — leave the
  // job pending for next cycle (operator may plug printer in).
  if (result?.skipped) {
    postEvent("bridge_skipped", {
      reason: result.reason || "unspecified",
      error: result.code,
      diag: result.diag,
    });
    return false;
  }
  if (!result?.ok) {
    postEvent("bridge_failed", {
      reason: result?.reason, error: result?.error || result?.code,
      diag: result?.diag,
    });
    await api.post(`/print/jobs/${job.id}/failed`, {
      error: result?.reason || "bridge_error",
      device_id: _devId,
    });
    const label =
      _t === "kitchen_docket" ? "Kitchen docket"
      : _t === "customer_receipt" ? "Receipt"
      : _t.startsWith("customer_package_receipt") ? "Package label"
      : _t.startsWith("kitchen_docket_update") ? "Updated docket"
      : _t.startsWith("customer_receipt_update") ? "Updated receipt"
      : _t.startsWith("void_slip_") ? "Void slip"
      : _t.startsWith("kitchen_docket_course.") ? "FIRE course docket"
      : "Print";
    toast.error(`Print failed — ${label} ticket #${job.ticket_number ?? "?"}.`);
    return false;
  }
  // iter_115 (2026-06-10) — Post bridge_success BEFORE marking the job
  // printed so the timeline reads: worker_seen → bridge_started →
  // bridge_success → marked_printed. Fire-and-forget; we do NOT block
  // on the event POST even if it fails.
  postEvent("bridge_success", {
    reason: result?.ticketNumber ? `ticket_${result.ticketNumber}` : undefined,
    diag: result?.diag,
  });
  await api.post(`/print/jobs/${job.id}/printed`);
  return true;
}

export function usePrintQueueWorker({ venue, enabled = true }) {
  const venueIdRef = useRef(venue?.id);
  const settingsRef = useRef(venue?.settings);
  useEffect(() => { venueIdRef.current = venue?.id; }, [venue?.id]);
  useEffect(() => { settingsRef.current = venue?.settings; }, [venue?.settings]);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;

    const cycle = async () => {
      if (stopped || _state.busy) return;
      _state.busy = true;
      try {
        const vid = venueIdRef.current;
        if (!vid) return;
        const r = await api.get("/print/jobs/pending", {
          params: { venue_id: vid, limit: 5 },
        });
        const jobs = r?.data?.jobs || [];
        if (!jobs.length) return;
        const vs = settingsRef.current || {};
        // Process sequentially so a stuck printer doesn't fire 5 jobs at once.
        for (const j of jobs) {
          if (stopped) break;
          await _processOne(j, vs);
        }
      } catch (e) {
        // Network blip / 401 — silent, retry next cycle.
      } finally {
        _state.busy = false;
      }
    };

    const t = setInterval(cycle, POLL_MS);
    // Fire once immediately so the operator doesn't wait the full poll.
    cycle();
    return () => { stopped = true; clearInterval(t); };
  }, [enabled]);
}
